import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { AccessRestricted } from '@/components/shared/AccessRestricted';

interface Props {
  permission: string;
  /** Route to redirect to when the actor lacks the required permission. */
  fallback?: string;
  /**
   * What to do when the permission is missing.
   *
   *  - `'redirect'` (default) — send the user to `fallback`. This is the
   *    original behaviour and remains the default precisely so that every
   *    pre-existing caller is unaffected: the routes that opted into a
   *    meaningful fallback (Job Pool → My Inspections, OPS Work Queue →
   *    Operations dashboard, and so on) still land there, because for those a
   *    neighbouring page really is the right destination.
   *
   *  - `'restrict'` — render `AccessRestricted` in place of the page. Correct
   *    where there is no neighbouring page that means the same thing: an
   *    administrative screen has no lesser version to fall back to, and
   *    redirecting a user who pasted `/organization/roles` to the dashboard
   *    looks like the app misfired rather than refused.
   *
   * Opt-in by design. A silent change of default would have altered the
   * behaviour of thirteen existing routes that were never audited for it.
   */
  onDenied?: 'redirect' | 'restrict';
}

/**
 * Renders child routes only when the authenticated user holds the required
 * permission. Wildcard (`*`) owners always pass.
 *
 * ── THIS IS A REAL BOUNDARY, NOT A CONVENIENCE ──────────────────────────────
 * It is the route-level half of the fix for the direct-URL finding recorded in
 * `RBAC_DIRECT_URL_AND_CUSTOMER_PORTAL_AUTHORIZATION_AUDIT_20260902.md`:
 * fifteen sensitive routes were reachable by pasting a URL, because hiding a
 * sidebar item is not authorization. Because this component is the route's
 * `element`, the decision is made BEFORE the page component mounts — the
 * protected page never renders, never mounts its queries and never paints.
 *
 * It is not, however, the security boundary. The backend `PermissionGuard`
 * remains authoritative and answers 403 no matter what this renders; the audit
 * verified that independently. What this adds is that an unauthorized user is
 * told so, instead of being shown an administrative screen full of nothing.
 *
 * Permissions only — never `roleSlug`. Role names are not an authorization
 * input anywhere in this application, and adding one here would create a second
 * model that could disagree with the backend's.
 */
export function PermissionRoute({ permission, fallback = '/', onDenied = 'redirect' }: Props) {
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);

  if (!permissions.includes('*') && !permissions.includes(permission)) {
    return onDenied === 'restrict' ? <AccessRestricted /> : <Navigate to={fallback} replace />;
  }
  return <Outlet />;
}
